import { SigninRedirectArgs, User, UserManager } from 'oidc-client-ts';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { PATHS } from '@/constants';
import oidcConfig from './oidcConfig';

interface AuthContextType {
  login: (redirectPath?: string) => Promise<void>;
  logout: () => void;
  setSessionUser: (user: User | null) => void;
  isAuthenticated: boolean;
  authLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const userManager = new UserManager(oidcConfig);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [loginInProgress, setLoginInProgress] = useState(false);
  const [logoutInProgress, setLogoutInProgress] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    userManager.getUser().then((loadedUser) => {
      if (loadedUser && !loadedUser.expired) {
        setUser(loadedUser);
      }
      setAuthLoading(false);
    });
  }, []);

  const loadUser = async () => {
    const loadedUser = await userManager.getUser();
    if (loadedUser && !loadedUser.expired) {
      setUser(loadedUser);
    }
  };

  useEffect(() => {
    loadUser();

    userManager.events.addUserLoaded(setUser);
    userManager.events.addUserUnloaded(() => setUser(null));
    userManager.events.addAccessTokenExpiring(async () => {
      try {
        const newUser = await userManager.signinSilent();
        setUser(newUser);
      } catch (error) {
        logout();
      }
    });

    return () => {
      userManager.events.removeUserLoaded(setUser);
    };
  }, []);

  const setSessionUser = useCallback((nextUser: User | null) => {
    setUser(nextUser);
  }, []);

  const login = useCallback(async (redirectPath?: string) => {
    if (import.meta.env.VITE_ENABLE_AUTH !== 'true') {
      console.warn('Authentication is disabled');
      return;
    }

    try {
      setLoginInProgress(true);

      // Don't start login if logout is in progress
      if (logoutInProgress) {
        return;
      }

      // Check if user is already authenticated
      const currentUser = await userManager.getUser();
      if (currentUser && !currentUser.expired) {
        // Keep React auth state in sync (e.g. after callback before userLoaded paints)
        setUser(currentUser);
        return;
      }

      // Try silent login only when a silent redirect URI is configured
      if (oidcConfig.silent_redirect_uri) {
        try {
          const silentUser = await userManager.signinSilent();
          if (silentUser && !silentUser.expired) {
            setUser(silentUser);
            return;
          }
        } catch {
          // Silent login failed, proceed with redirect
        }
      }

      // Prepare signin arguments with optional redirect
      const signinArgs: SigninRedirectArgs | undefined = redirectPath
        ? { state: { redirectUrl: redirectPath } }
        : undefined;

      // Redirect to login page
      await userManager.signinRedirect(signinArgs);
    } catch (error) {
      console.error('Login error:', error);

      // If a concurrent callback already established a session, do not send the user to /error
      const recoveredUser = await userManager.getUser();
      if (recoveredUser && !recoveredUser.expired) {
        setUser(recoveredUser);
        return;
      }

      navigate(PATHS.UNAUTH.ERROR);
    } finally {
      setLoginInProgress(false);
    }
  }, [logoutInProgress, navigate]);

  const logout = async () => {
    try {
      setLogoutInProgress(true);

      if (!loginInProgress) {
        // Get the current user to access their ID token
        const user = await userManager.getUser();

        // Prepare signout parameters with the ID token hint
        const signoutParams = {
          post_logout_redirect_uri: oidcConfig.post_logout_redirect_uri,
          id_token_hint: user?.id_token, // Include the ID token if available
        };

        setUser(null);

        // Remove the user from storage first
        await userManager.removeUser();

        // Then redirect to logout with the ID token hint
        await userManager.signoutRedirect(signoutParams);
      }
    } finally {
      setLogoutInProgress(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        setSessionUser,
        isAuthenticated: !!user && !user.expired,
        authLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
